import {
  Grid, Card, CardContent, Typography, Box, Chip, Table, TableBody,
  TableCell, TableHead, TableRow, CircularProgress, Alert, Button, Divider,
  LinearProgress, TextField, IconButton,
} from '@mui/material';
import {
  TrendingUp, TrendingDown, Inventory, People, LocalShipping, Warning,
  PointOfSale, AddBox, AccountBalance, EventBusy, EmojiEvents, ArrowUpward, ArrowDownward,
  TrackChanges, Edit,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, AreaChart, Area,
} from 'recharts';
import api from '../../api/axios';

const fmt = (n) => new Intl.NumberFormat('en-PK').format(Math.round(n || 0));

function CompareCard({ title, today, yesterday, icon, color, suffix = '' }) {
  const pct = yesterday > 0 ? (((today - yesterday) / yesterday) * 100).toFixed(1) : today > 0 ? 100 : 0;
  const up = today >= yesterday;
  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>{title}</Typography>
            <Typography variant="h5" fontWeight={700} color={`${color}.main`}>
              {suffix}{fmt(today)}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
              {up ? <ArrowUpward sx={{ fontSize: 14, color: 'success.main' }} /> : <ArrowDownward sx={{ fontSize: 14, color: 'error.main' }} />}
              <Typography variant="caption" color={up ? 'success.main' : 'error.main'} fontWeight={600}>
                {Math.abs(pct)}% vs yesterday
              </Typography>
            </Box>
            <Typography variant="caption" color="text.disabled">Yesterday: {suffix}{fmt(yesterday)}</Typography>
          </Box>
          <Box sx={{ p: 1.5, bgcolor: `${color}.50`, borderRadius: 2, color: `${color}.main` }}>{icon}</Box>
        </Box>
      </CardContent>
    </Card>
  );
}

function StatCard({ title, value, subtitle, icon, color, onClick }) {
  return (
    <Card sx={{ cursor: onClick ? 'pointer' : 'default' }} onClick={onClick}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>{title}</Typography>
            <Typography variant="h5" fontWeight={700}>{value}</Typography>
            {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
          </Box>
          <Box sx={{ p: 1.5, bgcolor: `${color}.50`, borderRadius: 2, color: `${color}.main` }}>{icon}</Box>
        </Box>
      </CardContent>
    </Card>
  );
}

function SalesTargetCard({ todaySales }) {
  const [target, setTarget] = useState(() => +(localStorage.getItem('pos_daily_target') || 0));
  const [editing, setEditing] = useState(false);
  const [editVal, setEditVal] = useState('');

  const saveTarget = () => {
    const v = +editVal;
    if (!isNaN(v) && v >= 0) {
      setTarget(v);
      localStorage.setItem('pos_daily_target', v);
    }
    setEditing(false);
  };

  const pct = target > 0 ? Math.min(100, (todaySales / target) * 100) : 0;
  const color = pct >= 100 ? 'success' : pct >= 75 ? 'primary' : pct >= 50 ? 'warning' : 'error';
  const remaining = Math.max(0, target - todaySales);

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TrackChanges color={color} />
            <Typography variant="body2" color="text.secondary" fontWeight={600}>Daily Sales Target</Typography>
          </Box>
          <IconButton size="small" onClick={() => { setEditVal(target || ''); setEditing(true); }}><Edit fontSize="small" /></IconButton>
        </Box>

        {editing ? (
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <TextField size="small" type="number" label="Target (₨)" value={editVal}
              onChange={e => setEditVal(e.target.value)} inputProps={{ min: 0 }} sx={{ flex: 1 }}
              onKeyDown={e => e.key === 'Enter' && saveTarget()} autoFocus />
            <Button size="small" variant="contained" onClick={saveTarget}>Set</Button>
            <Button size="small" onClick={() => setEditing(false)}>✕</Button>
          </Box>
        ) : target > 0 ? (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="h6" fontWeight={700} color={`${color}.main`}>
                ₨ {new Intl.NumberFormat('en-PK').format(Math.round(todaySales))}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                / ₨ {new Intl.NumberFormat('en-PK').format(target)}
              </Typography>
            </Box>
            <LinearProgress variant="determinate" value={pct} color={color} sx={{ height: 8, borderRadius: 4, mb: 0.5 }} />
            <Typography variant="caption" color={pct >= 100 ? 'success.main' : 'text.secondary'}>
              {pct >= 100 ? '🎉 Target achieved!' : `${pct.toFixed(0)}% — ₨ ${new Intl.NumberFormat('en-PK').format(Math.round(remaining))} remaining`}
            </Typography>
          </>
        ) : (
          <Typography variant="body2" color="text.disabled" sx={{ mt: 1 }}>
            Click ✎ to set a daily target
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

export default function DashboardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get('/dashboard')
      .then(r => setData(r.data.data))
      .catch(e => setErr(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  if (err) return <Alert severity="error">{err}</Alert>;
  if (!data) return null;

  const weekPct = data.comparison.lastWeek > 0
    ? (((data.comparison.thisWeek - data.comparison.lastWeek) / data.comparison.lastWeek) * 100).toFixed(1)
    : data.comparison.thisWeek > 0 ? 100 : 0;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h5" fontWeight={700}>{t('nav.dashboard')}</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="contained" startIcon={<PointOfSale />} onClick={() => navigate('/pos')}>New Sale</Button>
          <Button variant="outlined" startIcon={<AddBox />} onClick={() => navigate('/products')}>Add Product</Button>
        </Box>
      </Box>

      {/* Alert banners */}
      {(data.alerts.low_stock > 0 || data.alerts.expiry_count > 0 || data.dues.customer_dues > 0) && (
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          {data.alerts.low_stock > 0 && (
            <Alert severity="warning" action={<Button size="small" onClick={() => navigate('/inventory/reorder')}>View</Button>}>
              <strong>{data.alerts.low_stock}</strong> products below minimum stock level
            </Alert>
          )}
          {data.alerts.expiry_count > 0 && (
            <Alert severity="error" action={<Button size="small" onClick={() => navigate('/inventory/expiry')}>View</Button>}>
              <strong>{data.alerts.expiry_count}</strong> products expiring within 30 days
            </Alert>
          )}
          {data.dues.customer_dues > 0 && (
            <Alert severity="info" action={<Button size="small" onClick={() => navigate('/ledger')}>View</Button>}>
              Customer dues: <strong>₨ {fmt(data.dues.customer_dues)}</strong>
            </Alert>
          )}
        </Box>
      )}

      {/* Today vs Yesterday comparison cards */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <CompareCard
            title="Today's Sales"
            today={data.summary.todaySales.total}
            yesterday={data.summary.yesterdaySales.total}
            icon={<TrendingUp />} color="primary" suffix="₨ "
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <CompareCard
            title="Today's Invoices"
            today={data.summary.todaySales.count}
            yesterday={data.summary.yesterdaySales.count}
            icon={<PointOfSale />} color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>This Week</Typography>
              <Typography variant="h5" fontWeight={700} color="success.main">₨ {fmt(data.comparison.thisWeek)}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                {+weekPct >= 0
                  ? <ArrowUpward sx={{ fontSize: 14, color: 'success.main' }} />
                  : <ArrowDownward sx={{ fontSize: 14, color: 'error.main' }} />}
                <Typography variant="caption" color={+weekPct >= 0 ? 'success.main' : 'error.main'} fontWeight={600}>
                  {Math.abs(+weekPct)}% vs last week
                </Typography>
              </Box>
              <Typography variant="caption" color="text.disabled">Last week: ₨ {fmt(data.comparison.lastWeek)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>Monthly Sales</Typography>
              <Typography variant="h5" fontWeight={700} color="warning.main">₨ {fmt(data.summary.monthlySales.total)}</Typography>
              <Typography variant="caption" color="text.secondary">{data.summary.monthlySales.count} invoices this month</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Secondary stats */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} sm={3}>
          <StatCard title="Products" value={fmt(data.summary.products)} icon={<Inventory />} color="warning" onClick={() => navigate('/products')} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard title="Customers" value={fmt(data.summary.customers)} icon={<People />} color="secondary" onClick={() => navigate('/customers')} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            title="Customer Dues"
            value={`₨ ${fmt(data.dues.customer_dues)}`}
            subtitle={`Supplier: ₨ ${fmt(data.dues.supplier_dues)}`}
            icon={<AccountBalance />} color="error"
            onClick={() => navigate('/ledger')}
          />
        </Grid>
        {data.topCashier && (
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Box sx={{ p: 1.5, bgcolor: 'warning.50', borderRadius: 2, color: 'warning.main' }}><EmojiEvents /></Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Top Cashier Today</Typography>
                    <Typography variant="body1" fontWeight={700}>{data.topCashier.name}</Typography>
                    <Typography variant="caption" color="success.main">₨ {fmt(data.topCashier.revenue)} · {data.topCashier.invoices} sales</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}
        <Grid item xs={12}>
          <SalesTargetCard todaySales={data.summary.todaySales.total} />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* 7-day area chart */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" mb={2}>7-Day Sales Trend</Typography>
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={data.charts.daily}>
                  <defs>
                    <linearGradient id="salesGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#6366f1" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={d => d?.slice(5)} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `₨${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={v => [`₨ ${fmt(v)}`, 'Revenue']} />
                  <Area type="monotone" dataKey="total" stroke="#6366f1" strokeWidth={2} fill="url(#salesGrad)" dot={{ r: 4, fill: '#6366f1' }} />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Low stock + alerts */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Warning color="warning" />
                  <Typography variant="h6">{t('dashboard.lowStock')}</Typography>
                </Box>
                <Button size="small" onClick={() => navigate('/inventory/reorder')}>All</Button>
              </Box>
              {data.lowStock.length === 0
                ? <Typography variant="body2" color="text.secondary">All products well-stocked ✓</Typography>
                : data.lowStock.map(item => (
                  <Box key={item.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 160 }}>{item.name}</Typography>
                    <Chip label={item.stock <= 0 ? 'OUT' : `${item.stock} left`} size="small"
                      color={item.stock <= 0 ? 'error' : 'warning'} />
                  </Box>
                ))
              }
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Sales */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">{t('dashboard.recentSales')}</Typography>
                <Button size="small" onClick={() => navigate('/sales')}>View All</Button>
              </Box>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Invoice #</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.recentSales.map(sale => (
                    <TableRow key={sale.id} hover>
                      <TableCell><Typography variant="body2" fontWeight={600} color="primary">{sale.invoice_number}</Typography></TableCell>
                      <TableCell>{sale.customer_name || <Typography variant="body2" color="text.secondary">Walk-in</Typography>}</TableCell>
                      <TableCell align="right"><Typography fontWeight={700}>₨ {fmt(sale.total_amount)}</Typography></TableCell>
                      <TableCell><Typography variant="caption" color="text.secondary">{new Date(sale.created_at).toLocaleDateString('en-PK')}</Typography></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Grid>

        {/* Top Products */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" mb={2}>{t('dashboard.topProducts')} (30d)</Typography>
              {data.topProducts.length === 0
                ? <Typography color="text.secondary" variant="body2">No sales data yet</Typography>
                : data.topProducts.map((p, i) => (
                  <Box key={p.product_id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ width: 26, height: 26, bgcolor: i === 0 ? 'warning.main' : 'grey.200', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Typography variant="caption" color={i === 0 ? 'white' : 'text.primary'} fontWeight={700}>{i + 1}</Typography>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>{p.product_name}</Typography>
                      <Typography variant="caption" color="text.secondary">{p.total_qty} sold</Typography>
                    </Box>
                    <Typography variant="body2" fontWeight={700} color="success.main">₨ {fmt(p.total_revenue)}</Typography>
                  </Box>
                ))
              }
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
